import React, { useEffect, useState } from "react";
import {
    Box, Typography, Grid, CircularProgress, Slide, Chip,
    Avatar, Tooltip, Divider, Skeleton, Button
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
    PeopleAlt, Assignment, TrendingUp, Receipt,
    Group, WarningAmber, Schedule, HourglassEmpty,
    CheckCircleOutline, RadioButtonUnchecked
} from "@mui/icons-material";
import Layout from "./Layout";
import IncomeAreaChart from "./charts/IncomeAreaChart";
import DashboardService from "../services/DashboardService";
import InsightService from "../services/InsightService";
import { useToast } from "../contexts/ToastContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { glassSurface, glassHover, brandTintGlass } from "../styles/glass";

function formatCHF(n, locale) {
    if (n == null) return "—";
    try {
        return new Intl.NumberFormat(locale || "de-CH", {
            style: "currency",
            currency: "CHF",
            maximumFractionDigits: 0,
        }).format(n);
    } catch {
        return `CHF ${Math.round(n).toLocaleString("de-CH")}`;
    }
}

function localeFromLng(lng) {
    const base = (lng || "en").split("-")[0];
    switch (base) {
        case "de": return "de-CH";
        case "it": return "it-CH";
        case "fr": return "fr-CH";
        default:   return "en-CH";
    }
}

function daysUntil(dateStr) {
    if (!dateStr) return null;
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

function initials(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    return (parts.length >= 2
        ? parts[0][0] + parts[parts.length - 1][0]
        : name.slice(0, 2)
    ).toUpperCase();
}

const AVATAR_COLORS = ["#427898", "#5b9ab5", "#3a6b87", "#6baed6", "#2d5470"];
function nameColor(name) {
    if (!name) return "#427898";
    return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
}

const panelSx = {
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    backgroundColor: "#fff",
    overflow: "hidden",
    height: "100%",
};

const sectionTitleSx = {
    borderLeft: "3px solid #427898",
    pl: 1.5,
    py: 0.25,
};

const BRIEFING_SEVERITY_COLORS = {
    CRITICAL:    "#EF4444",
    WARNING:     "#F59E0B",
    OPPORTUNITY: "#10B981",
    INFO:        "#6366F1",
};

const QUOTE_STATUS_COLORS = {
    DRAFT:             "#94a3b8",
    SENT:              "#3b82f6",
    AWAITING_SIGNATURE:"#f59e0b",
    SIGNED:            "#10b981",
    ACCEPTED:          "#10b981",
    CHANGES_REQUESTED: "#f97316",
    REJECTED:          "#ef4444",
    CANCELLED:         "#cbd5e1",
};

const CONTRACT_STATUS_COLORS = {
    DRAFT:             "#94a3b8",
    SIGNING:           "#f59e0b",
    CHANGES_REQUESTED: "#f97316",
    ACTIVE:            "#10b981",
    TERMINATED:        "#64748b",
    CANCELLED:         "#cbd5e1",
};

const INVOICE_STATUS_COLORS = {
    DRAFT:          "#94a3b8",
    ISSUED:         "#3b82f6",
    SENT:           "#6366f1",
    PARTIALLY_PAID: "#f59e0b",
    PAID:           "#10b981",
    OVERDUE:        "#ef4444",
    CANCELLED:      "#cbd5e1",
    WRITTEN_OFF:    "#64748b",
};

const TASK_STATUS_COLORS = {
    PLANNED:     "#3b82f6",
    IN_PROGRESS: "#f59e0b",
    DONE:        "#10b981",
    CANCELLED:   "#cbd5e1",
};

function StatusDot({ color }) {
    return <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />;
}

function PanelHeader({ title, count, countColor }) {
    return (
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={sectionTitleSx}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1a2332" }}>
                    {title}
                </Typography>
            </Box>
            {count != null && count > 0 && (
                <Box sx={{ px: 1, py: 0.25, borderRadius: "6px", backgroundColor: countColor || "#fef2f2", color: countColor ? "#fff" : "#ef4444", fontSize: 11, fontWeight: 700 }}>
                    {count}
                </Box>
            )}
        </Box>
    );
}

const HomePage = () => {
    const { i18n } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const currentLocale = localeFromLng(i18n.language);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [welcomeName, setWelcomeName] = useState(null);
    const [welcomeVisible, setWelcomeVisible] = useState(false);
    const [briefing, setBriefing] = useState([]);
    const [briefingLoading, setBriefingLoading] = useState(true);

    useEffect(() => {
        DashboardService.getDashboard()
            .then(setData)
            .catch(() => showToast("Failed to load dashboard", "warning"))
            .finally(() => setLoading(false));

        InsightService.getBriefing()
            .then(res => setBriefing(res?.data || res || []))
            .catch(() => setBriefing([]))
            .finally(() => setBriefingLoading(false));

        const name = sessionStorage.getItem("SHOW_WELCOME");
        if (name !== null) {
            sessionStorage.removeItem("SHOW_WELCOME");
            setWelcomeName(name);
            setWelcomeVisible(true);
            setTimeout(() => setWelcomeVisible(false), 5000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const alerts = data?.alerts;
    const kpi = data?.kpi;
    const financial = data?.financial;
    const pipeline = data?.pipeline;
    const recentProjects = data?.recentProjects || [];
    const todaysTasks = data?.todaysTasks || [];

    const alertItems = alerts ? [
        alerts.overdueInvoicesCount > 0 && {
            label: `${alerts.overdueInvoicesCount} overdue invoice${alerts.overdueInvoicesCount !== 1 ? "s" : ""}`,
            sublabel: formatCHF(alerts.overdueInvoicesAmount, currentLocale),
            color: "#ef4444",
            bg: "#fef2f2",
            icon: <WarningAmber sx={{ fontSize: 14 }} />,
            onClick: () => navigate("/projects"),
        },
        alerts.expiringQuotesCount > 0 && {
            label: `${alerts.expiringQuotesCount} quote${alerts.expiringQuotesCount !== 1 ? "s" : ""} expiring`,
            sublabel: "within 7 days",
            color: "#f59e0b",
            bg: "#fffbeb",
            icon: <HourglassEmpty sx={{ fontSize: 14 }} />,
            onClick: () => navigate("/quotes"),
        },
        alerts.contractsAwaitingSignatureCount > 0 && {
            label: `${alerts.contractsAwaitingSignatureCount} contract${alerts.contractsAwaitingSignatureCount !== 1 ? "s" : ""} awaiting signature`,
            color: "#6366f1",
            bg: "#eef2ff",
            icon: <Schedule sx={{ fontSize: 14 }} />,
            onClick: () => navigate("/contracts"),
        },
    ].filter(Boolean) : [];

    const kpiCards = kpi ? [
        { title: "Customers", value: kpi.totalCustomers, icon: <PeopleAlt sx={{ fontSize: 22, color: "#427898" }} />, onClick: () => navigate("/customers") },
        { title: "Active Projects", value: kpi.activeProjects, icon: <Assignment sx={{ fontSize: 22, color: "#427898" }} />, onClick: () => navigate("/projects") },
        { title: "Open Quotes", value: `${kpi.openQuotesCount}  ·  ${formatCHF(kpi.openQuotesValue, currentLocale)}`, icon: <TrendingUp sx={{ fontSize: 22, color: "#427898" }} />, onClick: () => navigate("/quotes") },
        { title: "Outstanding", value: formatCHF(kpi.outstandingInvoicesAmount, currentLocale), icon: <Receipt sx={{ fontSize: 22, color: kpi.outstandingInvoicesAmount > 0 ? "#ef4444" : "#427898" }} />, onClick: () => navigate("/projects"), highlight: kpi.outstandingInvoicesAmount > 0 },
        { title: "Employees", value: kpi.totalEmployees, icon: <Group sx={{ fontSize: 22, color: "#427898" }} />, onClick: () => navigate("/employees") },
    ] : [];

    const quoteStatusOrder = ["DRAFT", "SENT", "AWAITING_SIGNATURE", "SIGNED", "ACCEPTED", "CHANGES_REQUESTED", "REJECTED"];
    const contractStatusOrder = ["DRAFT", "SIGNING", "CHANGES_REQUESTED", "ACTIVE", "TERMINATED", "CANCELLED"];
    const invoiceStatusOrder = ["DRAFT", "ISSUED", "SENT", "PARTIALLY_PAID", "PAID", "OVERDUE", "WRITTEN_OFF"];

    return (
        <Layout>
            {/* Welcome banner */}
            <Slide direction="down" in={welcomeVisible} mountOnEnter unmountOnExit>
                <Box sx={{
                    position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)",
                    zIndex: 1400, backgroundColor: "#427898", color: "white",
                    px: 4, py: 2, borderRadius: 3, boxShadow: 6,
                    display: "flex", alignItems: "center", gap: 1.5,
                    minWidth: 320, justifyContent: "center",
                }}>
                    <RocketLaunchIcon sx={{ fontSize: 22 }} />
                    <Typography fontWeight={600} fontSize={16}>
                        Welcome to Plankton CRM{welcomeName ? `, ${welcomeName}` : ""}!
                    </Typography>
                </Box>
            </Slide>

            <Box sx={{ p: 3 }}>

                {/* ── Alerts strip ── */}
                {!loading && alertItems.length > 0 && (
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
                        {alertItems.map((a, i) => (
                            <Box
                                key={i}
                                onClick={a.onClick}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    px: 2, py: 1, borderRadius: "10px",
                                    backgroundColor: a.bg, border: `1px solid ${a.color}30`,
                                    cursor: "pointer", flexShrink: 0,
                                    transition: "opacity .15s",
                                    "&:hover": { opacity: 0.8 },
                                }}
                            >
                                <Box sx={{ color: a.color, display: "flex", alignItems: "center" }}>{a.icon}</Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: a.color }}>{a.label}</Typography>
                                {a.sublabel && (
                                    <Typography sx={{ fontSize: 12, color: a.color, opacity: 0.75, ml: 0.25 }}>
                                        {a.sublabel}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* ── Today's AI Briefing ── */}
                {(briefingLoading || briefing.length > 0) && (
                    <Box sx={{ ...brandTintGlass, mb: 3, p: 0, overflow: "hidden", height: "auto" }}>
                        <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ...sectionTitleSx }}>
                                <AutoAwesomeIcon sx={{ fontSize: 15, color: "#427898" }} />
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1a2332" }}>
                                    Today's AI Briefing
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="text"
                                endIcon={<AutoAwesomeIcon sx={{ fontSize: 13 }} />}
                                onClick={() => {
                                    // Opens the AgentDrawer on the Insights tab.
                                    // App.js listens for this event and sets defaultTab='insights'.
                                    window.dispatchEvent(new CustomEvent("open-agent-drawer", { detail: { tab: "insights" } }));
                                }}
                                sx={{
                                    fontSize: 12,
                                    color: "#427898",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    py: 0.25,
                                    px: 1,
                                    "& .MuiButton-endIcon": { ml: 0.5 },
                                    "&:hover": { bgcolor: "#eef4f8" },
                                }}
                            >
                                Open AI Insights →
                            </Button>
                        </Box>

                        <Box sx={{ px: 2.5, py: 1.5 }}>
                            {briefingLoading ? (
                                <>
                                    <Skeleton variant="rounded" height={32} sx={{ mb: 1, borderRadius: 1, bgcolor: "#f1f5f9" }} />
                                    <Skeleton variant="rounded" height={32} sx={{ mb: 1, borderRadius: 1, bgcolor: "#f1f5f9" }} />
                                    <Skeleton variant="rounded" height={32} sx={{ borderRadius: 1, bgcolor: "#f1f5f9" }} />
                                </>
                            ) : (
                                briefing.slice(0, 5).map((item, i) => {
                                    const dotColor = BRIEFING_SEVERITY_COLORS[item.severity] || "#94a3b8";
                                    return (
                                        <Box
                                            key={item.id || i}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                py: 1,
                                                borderBottom: i < Math.min(briefing.length, 5) - 1 ? "1px solid #f1f5f9" : "none",
                                            }}
                                        >
                                            {/* Severity dot */}
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: "50%",
                                                    backgroundColor: dotColor,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: "#1E293B",
                                                        lineHeight: 1.3,
                                                    }}
                                                    noWrap
                                                >
                                                    {item.title}
                                                </Typography>
                                                {item.description && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 11,
                                                            color: "#64748B",
                                                            lineHeight: 1.4,
                                                            mt: 0.15,
                                                        }}
                                                        noWrap
                                                    >
                                                        {item.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {item.category && (
                                                <Chip
                                                    label={item.category}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: 10,
                                                        fontWeight: 500,
                                                        backgroundColor: "#F1F5F9",
                                                        color: "#64748B",
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                )}

                {/* ── KPI cards ── */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {loading
                        ? Array.from({ length: 5 }).map((_, i) => (
                            <Grid item xs={12} sm={6} lg={12 / 5} key={i}>
                                <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", backgroundColor: "#fff", p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
                                    <Box sx={{ width: 48, height: 48, borderRadius: "12px", backgroundColor: "#f1f5f9", flexShrink: 0 }} />
                                    <CircularProgress size={18} sx={{ color: "#427898" }} />
                                </Box>
                            </Grid>
                        ))
                        : kpiCards.map(({ title, value, icon, onClick, highlight }) => (
                            <Grid item xs={12} sm={6} lg={12 / 5} key={title}>
                                <Box
                                    onClick={onClick}
                                    sx={{
                                        ...(highlight
                                            ? { border: "1px solid #fecaca", borderRadius: "18px", backgroundColor: "#fff5f5", transition: "box-shadow .2s, transform .2s" }
                                            : glassSurface),
                                        ...glassHover,
                                        p: 2.5, display: "flex", alignItems: "center", gap: 2,
                                    }}
                                >
                                    <Box sx={{
                                        width: 48, height: 48, borderRadius: "12px",
                                        backgroundColor: highlight ? "#fee2e2" : "#eef4f8",
                                        flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        {icon}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, lineHeight: 1, mb: 0.5 }}>
                                            {title}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={700} sx={{ color: highlight ? "#ef4444" : "#1a2332", lineHeight: 1.1, fontSize: "1.1rem" }} noWrap>
                                            {value ?? "—"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))
                    }
                </Grid>

                {/* ── Row 2: Revenue chart + Invoice breakdown ── */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>

                    {/* Revenue chart */}
                    <Grid item xs={12} md={8}>
                        <Box sx={{ ...panelSx, p: 3 }}>
                            <Box sx={{ ...sectionTitleSx, mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#1a2332" }}>
                                    Revenue Overview
                                </Typography>
                            </Box>
                            {loading
                                ? <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} sx={{ color: "#427898" }} /></Box>
                                : <IncomeAreaChart slot="month" data={financial?.revenueGrowth || []} />
                            }
                        </Box>
                    </Grid>

                    {/* Invoice breakdown */}
                    <Grid item xs={12} md={4}>
                        <Box sx={panelSx}>
                            <PanelHeader title="Invoice Status" />
                            <Box sx={{ p: 2.5 }}>
                                {loading
                                    ? <CircularProgress size={20} sx={{ color: "#427898" }} />
                                    : (() => {
                                        const breakdown = financial?.invoiceBreakdown || {};
                                        const total = Object.values(breakdown).reduce((s, c) => s + c, 0);
                                        return (
                                            <>
                                                {/* Month comparison */}
                                                <Box sx={{ mb: 2.5, pb: 2, borderBottom: "1px solid #f1f5f9" }}>
                                                    <Box sx={{ display: "flex", gap: 3 }}>
                                                        <Box>
                                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>This Month</Typography>
                                                            <Typography variant="h6" fontWeight={700} sx={{ color: "#1a2332", fontSize: "1rem" }}>
                                                                {formatCHF(financial?.revenueThisMonth || 0, currentLocale)}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, mb: 0.5 }}>Last Month</Typography>
                                                            <Typography variant="h6" fontWeight={700} sx={{ color: "#64748b", fontSize: "1rem" }}>
                                                                {formatCHF(financial?.revenueLastMonth || 0, currentLocale)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                {/* Status list */}
                                                {invoiceStatusOrder
                                                    .filter(s => breakdown[s] > 0)
                                                    .map(status => {
                                                        const count = breakdown[status] || 0;
                                                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                                        const color = INVOICE_STATUS_COLORS[status] || "#94a3b8";
                                                        return (
                                                            <Box key={status} sx={{ mb: 1.5 }}>
                                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                        <StatusDot color={color} />
                                                                        <Typography sx={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>
                                                                            {status.replace(/_/g, " ")}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1a2332" }}>{count}</Typography>
                                                                </Box>
                                                                <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#f1f5f9", overflow: "hidden" }}>
                                                                    <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: color, borderRadius: 2 }} />
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}
                                                {Object.keys(breakdown).length === 0 && (
                                                    <Typography variant="body2" sx={{ color: "#94a3b8" }}>No invoices yet</Typography>
                                                )}
                                            </>
                                        );
                                    })()
                                }
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* ── Row 3: Pipeline + Active projects ── */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>

                    {/* Sales pipeline */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ ...panelSx, height: "auto" }}>
                            <PanelHeader title="Sales Pipeline" />
                            <Box sx={{ p: 2.5 }}>
                                {loading
                                    ? <CircularProgress size={20} sx={{ color: "#427898" }} />
                                    : (
                                        <>
                                            {/* Quotes */}
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, mb: 1.5 }}>
                                                Quotes
                                            </Typography>
                                            {(() => {
                                                const qs = pipeline?.quotesByStatus || {};
                                                const shown = quoteStatusOrder.filter(s => qs[s] > 0);
                                                if (shown.length === 0) return <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2 }}>No active quotes</Typography>;
                                                return shown.map(status => (
                                                    <Box key={status} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <StatusDot color={QUOTE_STATUS_COLORS[status] || "#94a3b8"} />
                                                            <Typography sx={{ fontSize: 12, color: "#475569" }}>
                                                                {status.replace(/_/g, " ")}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={qs[status]}
                                                            size="small"
                                                            sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: `${QUOTE_STATUS_COLORS[status]}18`, color: QUOTE_STATUS_COLORS[status] }}
                                                        />
                                                    </Box>
                                                ));
                                            })()}

                                            <Divider sx={{ my: 2 }} />

                                            {/* Contracts */}
                                            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, mb: 1.5 }}>
                                                Contracts
                                            </Typography>
                                            {(() => {
                                                const cs = pipeline?.contractsByStatus || {};
                                                const shown = contractStatusOrder.filter(s => cs[s] > 0);
                                                if (shown.length === 0) return <Typography variant="body2" sx={{ color: "#94a3b8" }}>No contracts</Typography>;
                                                return shown.map(status => (
                                                    <Box key={status} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                            <StatusDot color={CONTRACT_STATUS_COLORS[status] || "#94a3b8"} />
                                                            <Typography sx={{ fontSize: 12, color: "#475569" }}>
                                                                {status.replace(/_/g, " ")}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={cs[status]}
                                                            size="small"
                                                            sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: `${CONTRACT_STATUS_COLORS[status]}18`, color: CONTRACT_STATUS_COLORS[status] }}
                                                        />
                                                    </Box>
                                                ));
                                            })()}
                                        </>
                                    )
                                }
                            </Box>
                        </Box>
                    </Grid>

                    {/* Active projects */}
                    <Grid item xs={12} md={8}>
                        <Box sx={panelSx}>
                            <PanelHeader title="Active Projects" />
                            {loading
                                ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} sx={{ color: "#427898" }} /></Box>
                                : recentProjects.length === 0
                                    ? <Typography variant="body2" sx={{ color: "#94a3b8", px: 2.5, py: 2 }}>No active projects</Typography>
                                    : recentProjects.map((p, i) => {
                                        const days = daysUntil(p.endDate);
                                        const isOverdue = days !== null && days <= 0;
                                        const urgColor = isOverdue ? "#ef4444" : (days !== null && days <= 7) ? "#ef4444" : days <= 14 ? "#f59e0b" : "#64748b";
                                        const urgBg = isOverdue ? "#fef2f2" : (days !== null && days <= 7) ? "#fef2f2" : days <= 14 ? "#fffbeb" : "#f1f5f9";
                                        const urgLabel = isOverdue ? "Overdue" : days !== null ? `${days}d` : null;
                                        return (
                                            <Box
                                                key={p.id}
                                                onClick={() => navigate(`/project-info/${p.id}`)}
                                                sx={{
                                                    display: "flex", alignItems: "center", gap: 2,
                                                    px: 2.5, py: 1.75,
                                                    borderBottom: i < recentProjects.length - 1 ? "1px solid #f1f5f9" : "none",
                                                    cursor: "pointer",
                                                    "&:hover": { backgroundColor: "#f8fafc" },
                                                }}
                                            >
                                                <Avatar sx={{ width: 36, height: 36, fontSize: 13, fontWeight: 700, backgroundColor: nameColor(p.customerName), flexShrink: 0 }}>
                                                    {initials(p.customerName)}
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1a2332" }} noWrap>
                                                        {p.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }} noWrap>
                                                        {p.customerName}
                                                    </Typography>
                                                </Box>
                                                {urgLabel && (
                                                    <Box sx={{ px: 1, py: 0.3, borderRadius: "6px", backgroundColor: urgBg, color: urgColor, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                                        {urgLabel}
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })
                            }
                            {recentProjects.length > 0 && (
                                <Box onClick={() => navigate("/projects")} sx={{ px: 2.5, py: 1.5, cursor: "pointer", borderTop: "1px solid #f1f5f9", "&:hover": { backgroundColor: "#f8fafc" } }}>
                                    <Typography variant="body2" sx={{ color: "#427898", fontWeight: 600, fontSize: 12 }}>View all projects →</Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {/* ── Row 4: Today's tasks ── */}
                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <Box sx={panelSx}>
                            <PanelHeader
                                title="Today's Tasks"
                                count={alerts?.tasksTodayCount}
                                countColor="#427898"
                            />
                            {loading
                                ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} sx={{ color: "#427898" }} /></Box>
                                : todaysTasks.length === 0
                                    ? <Typography variant="body2" sx={{ color: "#94a3b8", px: 2.5, py: 2 }}>No tasks scheduled for today</Typography>
                                    : (
                                        <Grid container>
                                            {todaysTasks.map((task, i) => {
                                                const statusColor = TASK_STATUS_COLORS[task.status] || "#94a3b8";
                                                const isDone = task.status === "DONE";
                                                return (
                                                    <Grid item xs={12} sm={6} md={4} key={task.id}>
                                                        <Box sx={{
                                                            display: "flex", alignItems: "flex-start", gap: 1.5,
                                                            px: 2.5, py: 1.75,
                                                            borderBottom: "1px solid #f1f5f9",
                                                            borderRight: { md: i % 3 !== 2 ? "1px solid #f1f5f9" : "none", sm: i % 2 !== 1 ? "1px solid #f1f5f9" : "none", xs: "none" },
                                                        }}>
                                                            {isDone
                                                                ? <CheckCircleOutline sx={{ fontSize: 18, color: "#10b981", mt: 0.2, flexShrink: 0 }} />
                                                                : <RadioButtonUnchecked sx={{ fontSize: 18, color: statusColor, mt: 0.2, flexShrink: 0 }} />
                                                            }
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body2" fontWeight={600} sx={{ color: isDone ? "#94a3b8" : "#1a2332", textDecoration: isDone ? "line-through" : "none" }} noWrap>
                                                                    {task.title}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>{task.projectName}</Typography>
                                                                {task.assignedEmployeeNames?.length > 0 && (
                                                                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                                                                        {task.assignedEmployeeNames.map(name => (
                                                                            <Tooltip key={name} title={name}>
                                                                                <Avatar sx={{ width: 20, height: 20, fontSize: 9, fontWeight: 700, backgroundColor: nameColor(name) }}>
                                                                                    {initials(name)}
                                                                                </Avatar>
                                                                            </Tooltip>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            {task.startTime && (
                                                                <Typography sx={{ fontSize: 11, color: "#94a3b8", flexShrink: 0, mt: 0.2 }}>{task.startTime}</Typography>
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    )
                            }
                            <Box onClick={() => navigate("/planner")} sx={{ px: 2.5, py: 1.5, cursor: "pointer", borderTop: "1px solid #f1f5f9", "&:hover": { backgroundColor: "#f8fafc" } }}>
                                <Typography variant="body2" sx={{ color: "#427898", fontWeight: 600, fontSize: 12 }}>Open planner →</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

            </Box>
        </Layout>
    );
};

export default HomePage;
